// src/context/FilterContext.tsx
import React, { createContext, useContext, useReducer } from 'react';

interface FilterContextState {
  selectedChannels: string[];
  selectedMachineTypes: string[];
  selectedPlanograms: string[];
  selectedDateRange: { startDate: Date; endDate: Date; key: string };
}

const initialFilterContextState: FilterContextState = {
  selectedChannels: [],
  selectedMachineTypes: [],
  selectedPlanograms: [],
  selectedDateRange: {
    startDate: new Date(),
    endDate: new Date(),
    key: 'selection',
  },
};

// Define the filter actions
type FilterAction =
  | { type: 'SET_CHANNELS'; payload: string[] }
  | { type: 'SET_MACHINE_TYPES'; payload: string[] }
  | { type: 'SET_PLANOGRAMS'; payload: string[] }
  | { type: 'SET_DATE_RANGE'; payload: { startDate: Date; endDate: Date } };

// Define the reducer to manage the filter context
function filterReducer(
  state: FilterContextState,
  action: FilterAction,
): FilterContextState {
  switch (action.type) {
    case 'SET_CHANNELS':
      return { ...state, selectedChannels: action.payload };
    case 'SET_MACHINE_TYPES':
      return { ...state, selectedMachineTypes: action.payload };
    case 'SET_PLANOGRAMS':
      return { ...state, selectedPlanograms: action.payload };
    case 'SET_DATE_RANGE':
      return { ...state, selectedDateRange: action.payload };
    default:
      return state;
  }
}

// Create the FilterContext and provide methods to interact with it
const FilterContext = createContext<{
  state: FilterContextState;
  dispatch: React.Dispatch<FilterAction>;
}>({ state: initialFilterContextState, dispatch: () => {} });

export const FilterProvider: React.FC = ({ children }) => {
  const [state, dispatch] = useReducer(
    filterReducer,
    initialFilterContextState,
  );
  return (
    <FilterContext.Provider value={{ state, dispatch }}>
      {children}
    </FilterContext.Provider>
  );
};

// Custom hook to use the FilterContext
export const useFilterContext = () => useContext(FilterContext);
